import { usePersistedStore, useEphemeralStore, DEFAULT_DONOR_COUNTRY } from '@/lib/app-store';
import { createFileRoute } from '@tanstack/react-router';
import { DonateStepPending } from './-components/donate-step-pending';
import { useEffect, useRef, useState } from 'react';
import {
	type DropinConfiguration,
	AdyenCheckout,
	Dropin,
	Card,
	GooglePay,
	ApplePay,
	CashAppPay,
	type PaymentMethodsResponse,
	type ResultCode,
	type Order,
	type PaymentAction,
} from '@adyen/adyen-web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/lib/i18n';
import { useLingui } from '@lingui/react/macro';
import { errorMessages } from '@/lib/portal-messages';
import { toast } from '@/lib/toast';
import { logErrorFn, extractErrorInfo } from '@/lib/data';
import {
	isApplePaySessionAbortError,
	isClientFaultRpcError,
	setApplePayReporterMounted,
} from '@/lib/frontend-error-filter';
import { isVersionSkewReloadInProgress } from '../-components/default-catch-boundary';
import { trpc, trpcClient } from '@/lib/trpc';
import { getRuntimeAdyenClientKey } from '@/lib/shared';
import { Title } from '@worldcoo/ui/components/form/title';
import { Button } from '@worldcoo/ui/components/button';
import { CircleAlertIcon } from 'lucide-react';
import { DivWithPrimaryColorVariable } from './-components/div-with-primary-color-variable';
import { useServerFn } from '@tanstack/react-start';
import { generateCaptchaTokenResult } from '@/lib/captcha';
import { applicationType } from '@worldcoo/monolith/shared-context';
import { amountToMinorUnits } from '@worldcoo/shared/currencies';
import { localeToLanguage } from '@worldcoo/shared/core';
import { retry } from '@worldcoo/shared/utils';
import adyenCssUrl from '@adyen/adyen-web/styles/adyen.css?url';

const ENABLED_GOOGLE_PAY_PORTALS = new Set(['glovo', 'telefonica', 'rodilla', 'acciona-org', 'intress', 'angelman']);

const ADYEN_HOVER_PRELOADS: Array<{ rel: string; href: string; as?: string }> = [
	{ rel: 'preconnect', href: 'https://checkoutshopper-live.adyen.com' },
	{ rel: 'dns-prefetch', href: 'https://checkoutshopper-live.adyen.com' },
	{ rel: 'preload', href: adyenCssUrl, as: 'style' },
];

// Intent preload only fetches the route chunk; head() links are injected on
// match. Mirror the Adyen hints into <head> from the loader so hovering the
// "Next" button also warms the CDN connection and the stylesheet.
function injectAdyenHoverPreloads() {
	if (typeof document === 'undefined') return;
	for (const { rel, href, as } of ADYEN_HOVER_PRELOADS) {
		if (document.head.querySelector(`link[rel="${rel}"][href="${href}"]`)) continue;
		const link = document.createElement('link');
		link.rel = rel;
		link.href = href;
		if (as) link.as = as;
		document.head.appendChild(link);
	}
}

export const Route = createFileRoute('/$portalId/$campaignId/donate/checkout')({
	loader: () => {
		injectAdyenHoverPreloads();
	},
	head: () => ({
		meta: [{ name: 'robots', content: 'noindex' }],
		links: [...ADYEN_HOVER_PRELOADS, { rel: 'stylesheet', href: adyenCssUrl }],
	}),
	component: RouteComponent,
	pendingComponent: DonateStepPending,
});

function RouteComponent() {
	const { portalId, campaignId } = Route.useParams();
	const navigate = Route.useNavigate();
	const queryClient = useQueryClient();
	const hasHydrated = usePersistedStore((state) => state._hasHydrated);
	const amount = usePersistedStore((state) => state.amount);
	const details = usePersistedStore((state) => state.details);
	const { t } = useLingui();
	const { language } = useTranslation();
	const { mutateAsync: preregisterDonation } = useMutation(trpc.payments.preregisterDonation.mutationOptions());
	const { mutateAsync: getPaymentDetails } = useMutation(trpc.payments.getPaymentDetails.mutationOptions());
	const { mutateAsync: processPayment } = useMutation(trpc.payments.processPayment.mutationOptions());
	const dropinContainerRef = useRef<HTMLDivElement>(null);
	const termsContent = usePersistedStore((state) => state.termsContent);
	const ngosTermsContent = usePersistedStore((state) => state.ngosTermsContent);
	const telefonicaTermsContents = usePersistedStore((state) => state.telefonicaTermsContent);
	const setDonationToConfirm = useEphemeralStore((store) => store.setDonationToConfirm);
	const getCachedPreregistration = useEphemeralStore((state) => state.getCachedPreregistration);
	const getCachedPaymentMethods = useEphemeralStore((state) => state.getCachedPaymentMethods);
	const clearCheckoutCache = useEphemeralStore((state) => state.clearCheckoutCache);
	const logError = useServerFn(logErrorFn);

	// A payment that reached a delivered failed final (Refused/Cancelled/Error, DR-15). While set, our
	// own failure panel replaces the Drop-in instead of Adyen's final status screen, whose message can
	// render as a literal "undefined" and which offers no way to retry from inside the form.
	const [paymentFailed, setPaymentFailed] = useState(false);

	const currencyCode = 'EUR'; // TODO!!!

	useEffect(() => {
		if (hasHydrated && !amount) navigate({ to: `/$portalId/$campaignId/donate`, params: { portalId, campaignId } });
	}, [navigate, portalId, campaignId, hasHydrated, amount]);

	// Adyen's Apple Pay service handles a failed merchant validation with
	// `console.error(e), this.session.abort(), this.options.onError(e)`. When WebKit throws
	// `InvalidAccessError` from `abort()` (the sheet is already closed), `onError` never runs, so
	// neither the toast below nor `onError`'s report happen and the donor gets no feedback at all
	// (TEC-3637). Catching the escaped rejection here is the only place we can still react to it, and
	// unlike the global handler we can name the payment path it came from.
	//
	// Deliberately its own effect rather than riding along in the Adyen init below: that one re-runs on
	// any of ~25 deps (a language switch, an edited donor detail), and re-registering this listener — plus
	// the global handler's skip window — has no reason to be coupled to remounting the Dropin.
	useEffect(() => {
		const handleApplePayValidationFailure = (event: PromiseRejectionEvent) => {
			if (isVersionSkewReloadInProgress()) return;
			const errorInfo = extractErrorInfo(event.reason);
			if (!isApplePaySessionAbortError(errorInfo.name, errorInfo.message, errorInfo.stack)) return;

			toast.error(t(errorMessages.applePayUnavailable), { duration: 15_000 });
			// Swallow a failed report: this runs inside `unhandledrejection`, so an unhandled rejection
			// here would re-enter the same event and get ticketed by the global handler as a second,
			// unrelated error.
			logError({
				data: {
					route: window.location.pathname,
					name: '[ADYEN CHECKOUT] applePayMerchantValidation: ' + errorInfo.name,
					message: errorInfo.message,
					stack: errorInfo.stack,
				},
			}).catch((logErr) => {
				console.error('Failed to log error to backend:', logErr);
			});
		};
		window.addEventListener('unhandledrejection', handleApplePayValidationFailure);
		// Tell the global handler this route owns the Apple Pay signature now, so it stops reporting it
		// (see `isApplePaySessionAbortError` in router.tsx). Scoped to the listener's lifetime: outside
		// it nobody would report the failure at all.
		setApplePayReporterMounted(true);

		return () => {
			window.removeEventListener('unhandledrejection', handleApplePayValidationFailure);
			setApplePayReporterMounted(false);
		};
	}, [t, logError]);

	useEffect(() => {
		console.time('Adyen Checkout initialization');

		// This init awaits up to four network round-trips before it can mount, so the route can go away
		// first — and with ~25 deps it can also be superseded mid-flight. Without this the final
		// `.mount(dropinContainerRef.current!)` ran against a null ref and Adyen threw "Component could not
		// mount. Root node was not found." at a donor who had simply pressed back (TEC-3694).
		let cancelled = false;
		let dropin: Dropin | undefined;

		// What Adyen is told the donor's country is, for BOTH calls below, so the payment methods fetched
		// and the checkout they are rendered in never disagree. Not `details.country` directly: that field
		// is only required for a fiscal certificate and renders `optional` otherwise, so a donor who never
		// asked for one legitimately leaves it empty — and Adyen refuses to initialize without a country
		// ("IMPLEMENTATION_ERROR: You must specify a countryCode"), which threw here, three times through
		// `retry`, and toasted at a donor who had done nothing wrong.
		//
		// Deliberately NOT applied to the donor's address in `preregisterDonation` below: that country goes
		// on a fiscal certificate, where inventing one would be worse than leaving it blank.
		const adyenCountryCode = details.country || DEFAULT_DONOR_COUNTRY;

		const initAdyenCheckout = async () => {
			const mintPreregistration = () =>
				generateCaptchaTokenResult().then(({ data: captchaToken }) => {
					if (!captchaToken || !amount) return;

					return preregisterDonation({
						certificated: details.certificate,
						amount: amount,
						campaignId,
						currency: currencyCode,
						applicationId: {
							type: applicationType.portal,
							host: portalId,
						},
						donor: {
							name: details.firstname,
							lastName: details.lastname,
							contact: {
								email: details.email,
								language: localeToLanguage[language],
								physicalAddress: {
									region: details.region,
									street: {
										name: details.streetName,
										number: details.streetNumber,
										type: '',
									},
									additionalInfo: details.additionalInfo,
									city: details.city,
									country: details.country,
									postalCode: details.cp,
									province: details.province,
								},
							},
							document: {
								number: details.documentNumber,
								type: details.documentType,
							},
							subscribeNewsletter: false, // TODO: we should ask for this?
						},
						checkbox: [
							termsContent && { type: 'termsAndConditions' as const, ...termsContent },
							ngosTermsContent && { type: 'ngoTermsAndConditions' as const, ...ngosTermsContent },
							telefonicaTermsContents && {
								type: 'telefonicaTermsAndConditions' as const,
								...telefonicaTermsContents,
							},
						].filter((value) => value !== undefined),
						captchaToken,
					});
				});

			/**
			 * A replacement for a token the server refused, for the donor's NEXT tap — which may never come.
			 * The no-op `catch` is what makes an abandoned one safe: it marks the rejection handled without
			 * consuming it, so the submit that awaits this still sees the failure, while a donor who simply
			 * gives up does not leave a rejected promise for the global handler to ticket as a crash.
			 */
			const remintPreregistration = () => {
				const preregistration = mintPreregistration();
				preregistration.catch(() => {});
				return preregistration;
			};

			// Reassignable, and read at submit time rather than captured: a token the server refuses is
			// replaced below, so the donor's next tap carries a fresh one instead of re-presenting the one
			// that was just refused. `getCachedPreregistration` withholds a preload old enough for the
			// server's expiry to be near, which is what keeps that refusal rare in the first place.
			const cachedPreregistration = getCachedPreregistration();
			let preloadPreregisterDonation = cachedPreregistration?.captchaPassed
				? Promise.resolve(cachedPreregistration)
				: mintPreregistration();

			if (!amount) return;

			// Use cached payment methods if available and not expired, otherwise fetch new ones
			const cached = getCachedPaymentMethods();
			const paymentMethods = await (async () => {
				if (cached && (cached as { captchaPassed?: boolean }).captchaPassed) return cached;
				const { data: captchaToken } = await generateCaptchaTokenResult();
				if (!captchaToken) return undefined;
				return trpcClient.payments.getPaymentMethods.mutate({
					amount: { value: amountToMinorUnits(amount, currencyCode), currency: currencyCode },
					countryCode: adyenCountryCode,
					locale: language,
					captchaToken,
				});
			})();

			if (cancelled) return;

			if (!(paymentMethods as { captchaPassed?: boolean } | undefined)?.captchaPassed) {
				toast.error(t(errorMessages.captchaFailed));
				return;
			}

			// Retry the Adyen SDK init a few times — it fetches config from Adyen's servers and can fail
			// on a transient network blip. Safe to repeat: it only constructs a checkout instance, with no
			// donation side effects (unlike preregister/getPaymentMethods, which are intentionally excluded).
			const checkout = await retry({ attempts: 3, delayInMs: 300 }, () => {
				// Returning rather than throwing: `retry` only rethrows on its LAST attempt, so a throw here
				// would sit through two rounds of backoff before anyone noticed. `if (!checkout) return` picks
				// it up instead.
				if (cancelled) return undefined;
				return AdyenCheckout({
					locale: language === 'es' ? 'es-ES' : language === 'ca' ? 'ca-ES' : language === 'it' ? 'it-IT' : 'en-US',
					countryCode: adyenCountryCode,
					environment: getRuntimeAdyenClientKey().startsWith('test_') ? 'test' : 'live',
					clientKey: getRuntimeAdyenClientKey(),
					paymentMethodsResponse: paymentMethods as PaymentMethodsResponse,
					amount: { value: amountToMinorUnits(amount, currencyCode), currency: currencyCode },
					onSubmit: async (state, _component, actions) => {
						try {
							const preregisterDonationResult = await preloadPreregisterDonation;

							if (!preregisterDonationResult?.captchaPassed) {
								toast.error(t(errorMessages.captchaFailed));
								actions.reject();
								return;
							}

							const { donationId, paymentToken } = preregisterDonationResult;

							// No captcha minted here, unlike every other call on this router: for Apple Pay this
							// callback runs inside Apple's 30-second authorization window, and a reCAPTCHA
							// `execute` plus Google's `siteverify` is a round-trip that window cannot spare. The
							// single-use `paymentToken` gates the endpoint instead — see `docs/payment-checkout.md`.
							const result = await processPayment({
								donationId,
								amount: {
									currency: currencyCode,
									value: amountToMinorUnits(amount, currencyCode),
								},
								paymentMethodData: state.data,
								returnUrl: window.location.origin,
								paymentToken,
							});

							const { resultCode, action, order, donationToken } = result;

							// If the /payments request request form your server is successful, you must call this to resolve whichever of the listed objects are available.
							// You must call this, even if the result of the payment is unsuccessful.
							actions.resolve({
								resultCode: resultCode as ResultCode,
								action: action as PaymentAction,
								order: order as Order,
								donationToken,
							});
						} catch (error) {
							// The server refused the payment token on purpose: expired, or issued for another charge
							// (see `docs/payment-checkout.md`, DR-13). Two things follow, and neither is the report below.
							//
							// It is not ours to ticket. `logError` runs the noise filter, not the RPC-code one, so a
							// FORBIDDEN reaching it becomes a `PORTAL_FRONTEND_ERROR` and a System error — exactly the
							// per-tap flood the FORBIDDEN mapping exists to prevent, arriving through the client
							// instead of the server. The global handlers already skip these; this catch reports
							// directly, so it has to skip them itself.
							//
							// And the refused token has to go: it is re-presented on every retry, so without replacing
							// it here the donor's next tap is refused identically, and the one after that, until they
							// reload the page.
							if (isClientFaultRpcError(error)) {
								clearCheckoutCache();
								preloadPreregisterDonation = remintPreregistration();
								toast.error(t(errorMessages.paymentSessionExpired), { duration: 15_000 });
								actions.reject();
								return;
							}
							console.error('onSubmit', error);
							const errorInfo = extractErrorInfo(error);
							logError({
								data: {
									route: window.location.pathname,
									name: '[ADYEN CHECKOUT] onSubmit: ' + errorInfo.name,
									message: errorInfo.message,
									stack: errorInfo.stack,
								},
							});
							actions.reject();
						}
					},
					onAdditionalDetails: async (state, _component, actions) => {
						try {
							const { data: captchaToken } = await generateCaptchaTokenResult();
							if (!captchaToken) {
								toast.error(t(errorMessages.captchaFailed));
								actions.reject();
								return;
							}

							const result = await getPaymentDetails({
								paymentDetailsData: state.data,
								captchaToken,
							});

							if (!result.captchaPassed) {
								toast.error(t(errorMessages.captchaFailed));
								actions.reject();
								return;
							}

							const { resultCode, action, order, donationToken } = result;

							// If the /payments/details request request from your server is successful, you must call this to resolve whichever of the listed objects are available.
							// You must call this, even if the result of the payment is unsuccessful.
							actions.resolve({
								resultCode: resultCode as ResultCode,
								action: action as PaymentAction,
								order: order as Order,
								donationToken,
							});
						} catch (error) {
							// Same reason as `onSubmit` above: a refusal the server made on purpose is the caller's
							// fault, and this catch reports directly, so it must skip it itself. Nothing to re-mint
							// here — this step carries no payment token.
							if (isClientFaultRpcError(error)) {
								toast.error(t(errorMessages.paymentSessionExpired), { duration: 15_000 });
								actions.reject();
								return;
							}
							console.error('onAdditionalDetails', error);
							const errorInfo = extractErrorInfo(error);
							logError({
								data: {
									route: window.location.pathname,
									name: '[ADYEN CHECKOUT] onAdditionalDetails: ' + errorInfo.name,
									message: errorInfo.message,
									stack: errorInfo.stack,
								},
							});
							actions.reject();
						}
					},
					onPaymentCompleted: async (result, component) => {
						const preregisterDonationResult = await preloadPreregisterDonation;
						if (!preregisterDonationResult?.captchaPassed) {
							toast.error(t(errorMessages.captchaFailed));
							return;
						}

						console.info(result, component);

						if (preregisterDonationResult?.donationId) {
							setDonationToConfirm({ id: preregisterDonationResult.donationId, campaignId });
						}

						navigate({ to: '/$portalId/$campaignId/donate/thanks', params: { portalId, campaignId } });
					},
					onPaymentFailed: (result, component) => {
						console.info(result, component);
						const resultCode = (result as { resultCode?: string } | undefined)?.resultCode;

						// A DELIVERED failed final (`Refused`, `Cancelled`, `Error`) spends the attempt: the
						// preregistration's donationId is Adyen's `reference` and its token the idempotency key, so
						// retrying with the same pair replays this outcome instead of charging (DR-15). A submit that
						// failed WITHOUT a resultCode (a dropped response) must keep its token instead, so the retry
						// stays the collapse-safe one from DR-10 and cannot double-charge an attempt that succeeded.
						if (resultCode === 'Refused' || resultCode === 'Cancelled' || resultCode === 'Error') {
							// The next attempt is prepared here rather than on the click, so "Try again" is instant: a
							// fresh preregistration replaces the spent pair (cleared from the cache too, so a later
							// re-init cannot seed itself from it), and the Drop-in goes back to its payment form.
							// `setStatus` has to run here: Adyen's `handleFailedResult` sets its own `error` status
							// immediately BEFORE this callback, so only a reset after it holds. Both happen while the
							// container is hidden behind the panel, so the donor sees neither change.
							clearCheckoutCache();
							preloadPreregisterDonation = remintPreregistration();
							component?.setStatus('ready');
							setPaymentFailed(true);
							// Not a system error, so it is not reported (DR-16). All three codes mean Adyen answered
							// and answered "not charged", the panel above has told the donor so, and the report was a
							// per-tap ticket carrying nothing but the code itself: `{"resultCode":"Error"}` opened
							// TEC-3663 and kept reopening it. Rates belong on a metric, never on one ticket per donor.
							return;
						}

						// Anything else reaching `onPaymentFailed` is genuinely unexpected: no resultCode at all, or
						// one this checkout does not handle. Those still report.
						logError({
							data: {
								route: window.location.pathname,
								name: '[ADYEN CHECKOUT] onPaymentFailed',
								message: JSON.stringify(result),
								stack: undefined,
							},
						});
					},
					onError: (error, component) => {
						// A superseded or unmounted run: `dropin.unmount()` above can make Adyen abandon in-flight
						// work (secured-field iframes, a wallet handshake) and report it here. Same rule as the
						// init catch — a run that lost its route has no donor to toast and nothing worth ticketing.
						if (cancelled) return;

						const errorInfo = extractErrorInfo(error);
						// Adyen routes a shopper-initiated cancellation through `onError` as well, as an
						// `AdyenCheckoutError` of type `CANCEL` (the type lands on `name`). Every wallet uses it:
						// Google Pay maps the Payment Request `CANCELED` status to it, so a donor closing the sheet
						// arrived here as `AbortError: User closed the Payment Request UI.` and both opened a System
						// error and showed that raw English string as a toast (TEC-3652); Apple Pay uses the same type
						// for `ApplePay UI dismissed`, which is why matching on the type supersedes that message check.
						// The donor closed the sheet themselves: nothing to report, and nothing to tell them.
						if (errorInfo.name === 'CANCEL') return;

						// Our copy, not Adyen's: `errorInfo.message` is an untranslated SDK string written for
						// developers ("secured field iframes have failed to load", TEC-3702) and none of them is
						// donor-actionable. The message still travels in the report below, where it is useful.
						toast.error(t(errorMessages.internal), { duration: 15_000 });
						console.error(errorInfo.name, errorInfo.message, errorInfo.stack, component);
						logError({
							data: {
								route: window.location.pathname,
								name: '[ADYEN CHECKOUT] onError: ' + errorInfo.name,
								message: errorInfo.message,
								stack: errorInfo.stack,
							},
						});
					},
				});
			});
			if (!checkout) return;

			// Apple Pay is bundled for every portal and Adyen decides whether it renders: Drop-in filters the
			// list on each component's `isAvailable()`, which is `ApplePaySession.canMakePayments()` here and
			// `isReadyToPay` for Google Pay. The viewport used to stand in for that, gating on `useIsMobile()`,
			// and it was wrong twice over. It is not what decides whether a device can pay — a Mac with Touch
			// ID can, a narrow desktop window cannot pay any better for being narrow — and because the hook
			// tracks the breakpoint LIVE it sat in this effect's dependency list, so every crossing of 768px
			// tore the Drop-in down and rebuilt it. Rotating a phone (393px portrait, 852px landscape) crosses
			// it, which discarded a half-typed card form mid-payment; it also meant every mobile donor built
			// the Drop-in twice, since `useIsMobile` must return `false` on the hydration render and corrects
			// itself immediately after.
			const paymentMethodComponents: DropinConfiguration['paymentMethodComponents'] = [ApplePay];

			if (ENABLED_GOOGLE_PAY_PORTALS.has(portalId)) {
				paymentMethodComponents.push(GooglePay);
			}

			paymentMethodComponents.push(Card, CashAppPay);

			// Read the container once and bail if this run was superseded or the route unmounted while the
			// awaits above were in flight. Mounting is the last thing we do, so there is nothing to undo.
			const dropinContainer = dropinContainerRef.current;
			if (cancelled || !dropinContainer) return;

			dropin = new Dropin(checkout, {
				paymentMethodComponents,
				// Wallets render as their own buttons ABOVE the list instead of as entries inside it, so the
				// donor sees Apple Pay / Google Pay AND the card fields at once. Without this, a portal with a
				// wallet enabled showed a chooser first and put the card form one click away, so the common
				// case (paying by card) got slower precisely on the portals we had just given wallets to.
				instantPaymentTypes: ['applepay', 'googlepay'],
				// ...and the card form is the one left expanded. Naming the type is deliberate over
				// `openFirstPaymentMethod`, which opens whatever /paymentMethods happened to return first.
				//
				// Safe HERE and not in the donors app: this option outranks everything else, because Adyen's
				// `PaymentMethodList` selects it and returns before it reaches the stored-card branch. Portals
				// checkout is anonymous — `enableStoreDetails` offers to save a card for later, it does not
				// render saved ones — so there is no stored selection to demote. The donors app is signed in
				// and does render them, which is why it takes `instantPaymentTypes` alone.
				openPaymentMethod: { type: 'scheme' },
				paymentMethodsConfiguration: {
					card: {
						hasHolderName: true,
						holderNameRequired: true,
						enableStoreDetails: true,
						hideCVC: false,
					},
				},
			});
			dropin.mount(dropinContainer);

			console.timeEnd('Adyen Checkout initialization');
		};

		initAdyenCheckout().catch((error) => {
			// Guard the fire-and-forget init so a network/SDK failure surfaces a toast instead of
			// escaping as an unhandled promise rejection (which the global handler reports as a system error).
			// A run that lost its route is not a failure worth telling anyone about.
			if (cancelled) return;

			const errorInfo = extractErrorInfo(error);
			toast.error(t(errorMessages.internal));
			logError({
				data: {
					route: window.location.pathname,
					name: '[ADYEN CHECKOUT] init: ' + errorInfo.name,
					message: errorInfo.message,
					stack: errorInfo.stack,
				},
			});
		});

		return () => {
			cancelled = true;
			// Only set if we got as far as mounting. `unmount` detaches the Adyen UI and releases its
			// listeners, so a re-run does not leave a zombie checkout live on a node React has dropped.
			dropin?.unmount();
		};
	}, [
		amount,
		campaignId,
		details.additionalInfo,
		details.certificate,
		details.city,
		details.country,
		details.cp,
		details.documentNumber,
		details.documentType,
		details.email,
		details.firstname,
		details.lastname,
		details.province,
		details.region,
		details.streetName,
		details.streetNumber,
		getPaymentDetails,
		language,
		navigate,
		ngosTermsContent,
		preregisterDonation,
		processPayment,
		queryClient,
		portalId,
		telefonicaTermsContents,
		termsContent,
		setDonationToConfirm,
		getCachedPreregistration,
		getCachedPaymentMethods,
		clearCheckoutCache,
		t,
		logError,
	]);

	return (
		<>
			<Title size="lg" className="text-center">
				{t`Payment method`}
			</Title>

			<DivWithPrimaryColorVariable className="min-h-107">
				{paymentFailed && (
					/* `min-h-107` repeats the container's own min height so the panel is centred in the box the
					   Drop-in would have filled, instead of sitting at the top of a mostly empty card. */
					<div className="flex min-h-107 flex-col items-center justify-center gap-6 px-4 text-center">
						{/* Same treatment the catch boundary gives an error, so a failed payment reads as one of
						    this app's error states rather than a stray paragraph. */}
						<div className="bg-destructive/10 flex size-12 items-center justify-center rounded-full">
							<CircleAlertIcon className="text-destructive size-6" />
						</div>
						<p className="max-w-md">{t(errorMessages.paymentNotCompleted)}</p>
						{/* Sized like the flow's other primary actions (the details step's "Next"). The click only
						    reveals the Drop-in, because the failure handler above already prepared it. */}
						<Button size="2xl" className="w-full min-w-fit lg:w-60" onClick={() => setPaymentFailed(false)}>
							{t`Try again`}
						</Button>
					</div>
				)}
				{/* Stays mounted while the panel is up: the reset Drop-in inside it is what "Try again" reveals. */}
				<div ref={dropinContainerRef} className={paymentFailed ? 'hidden' : undefined} />
			</DivWithPrimaryColorVariable>

			<p className="text-muted-foreground/50 mx-auto mt-auto flex max-w-md gap-2 text-center text-sm leading-none font-medium whitespace-pre-line">
				{t`protected by reCAPTCHA.`}
				<a href="https://policies.google.com/privacy" className="underline" target="_blank" rel="noreferrer noopener">
					{t`Privacy`}
				</a>
				<a href="https://policies.google.com/terms" className="underline" target="_blank" rel="noreferrer noopener">
					{t`Terms`}
				</a>
			</p>
		</>
	);
}
